import React, { useState } from 'react';
import componentStyles from './gameLogs.module.css';
import { getColumnName } from '../../utils/columnUtils';
import { getHistoricalMatchupColumns } from '../../utils/historicalMatchUpConfig';

// HistoricalMatchup displays head-to-head matchup history in a table.
// It expects `historicalMatchupStats.matchupHistory` to be an array of objects
// where each object represents a past game with consistent keys.
const HistoricalMatchup = ({ historicalMatchupStats, sportType }) => {
    // Normalize rows from the provided stats
    const rows = Array.isArray(historicalMatchupStats?.matchupHistory)
        ? historicalMatchupStats.matchupHistory
        : [];

    // Build columns from the first row
    const columnNames = rows.length > 0 ? Object.keys(rows[0]) : [];
    
    const [showFullStats, setShowFullStats] = useState(false);

    const getColumns = () => {
        let columns = [];
        const sport = sportType.toLowerCase();
        if (showFullStats) {
            columns = getHistoricalMatchupColumns(sport, true);
            return columns || columnNames;
        }
        else {
            columns = getHistoricalMatchupColumns(sport, false);
            return columns || columnNames;
        }
    }

    // Color-code common result-like fields
    const getResultColor = (value) => {
        if (value === 'W' || value === 'OVER') return 'var(--bloomberg-green)';
        if (value === 'L' || value === 'UNDER') return 'var(--bloomberg-red)';
        return 'var(--bloomberg-orange)';
    };

    // Format a single cell value based on its key
    const formatTableCell = (key, value) => {
        if (!key) return <span className={componentStyles.defaultCell}>{String(value ?? '')}</span>;
        const keyLower = key.toLowerCase();

        // Result-like fields
        if (keyLower.includes('ats') || keyLower.includes('total') || keyLower.includes('result')) {
            return (
                <span className={componentStyles.resultCell} style={{ color: getResultColor(value) }}>
                    {String(value ?? '')}
                </span>
            );
        }

        // Score-like fields
        if (keyLower.includes('score') || keyLower.includes('points')) {
            return <span className={componentStyles.scoreCell}>{String(value ?? '')}</span>;
        }

        // Default
        return <span className={componentStyles.defaultCell}>{String(value ?? '')}</span>;
    };

    // Render the header cells
    const headerCells = getColumns().map((columnName) => (
        <th key={columnName} className={componentStyles.tableHeader}>
            {getColumnName(columnName)}
        </th>
    ));

    // Render all row cells
    const tableRows = rows.map((row, rowIndex) => (
        <tr key={rowIndex} className={componentStyles.tableRow}>
            {getColumns().map((columnName) => (
                <td key={`${rowIndex}-${columnName}`} className={componentStyles.tableCell}>
                    {formatTableCell(columnName, row[columnName])}
                </td>
            ))}
        </tr>
    ));

    const displayMatchupInsights = () => {
        if (!historicalMatchupStats) return null;

        return (
            <div className={componentStyles.matchupInsights}>
                <h3>Matchup Insights</h3>
                <div className={componentStyles.insightsGrid}>
                    <div className={componentStyles.insightItem}>
                        <span className={componentStyles.insightLabel}>Record:</span>
                        <span className={componentStyles.insightValue}>{historicalMatchupStats.record}</span>
                    </div>
                    <div className={componentStyles.insightItem}>
                        <span className={componentStyles.insightLabel}>Score Avg:</span>
                        <span className={componentStyles.insightValue}>{historicalMatchupStats.scoreAvg}</span>
                    </div>
                    <div className={componentStyles.insightItem}>
                        <span className={componentStyles.insightLabel}>Totals Avg:</span>
                        <span className={componentStyles.insightValue}>{historicalMatchupStats.totalsAvg}</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={componentStyles.gameLogsContainer}>
            <div className={componentStyles.gameLogsHeader}>
                <h2>Historical Matchup</h2>
            </div>
            {displayMatchupInsights()}
            {/* disabling historical matchup button for now */}
            {/* <div className={componentStyles.statsToggle}>
                <button id={'statsToggleButton'} className={componentStyles.statsToggleButton} onClick={() => setShowFullStats(!showFullStats)}>{showFullStats ? 'Show Simple Stats' : 'Show Full Stats'}</button>
            </div> */}
            <div className={componentStyles.tableContainer}>
                <div className={componentStyles.tableWrapper}>
                    {rows.length > 0 ? (
                        <table className={componentStyles.gameLogsTable}>
                            <thead>
                                <tr>{headerCells}</tr>
                            </thead>
                            <tbody>{tableRows}</tbody>
                        </table>
                    ) : (
                        <div className={componentStyles.noDataMessage}>
                            No historical matchup data available.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HistoricalMatchup;
